/**
 * Language state + translation hook.
 * `lang` is a signal so any component reading `.value` (directly, or indirectly through useT())
 * during its render re-renders automatically when the language changes — no context needed.
 */
import { signal } from '@preact/signals';
import { getDict, t as translate } from '@birflypermit/shared/i18n';
import { tenant } from './tenant';

const STORAGE_KEY = 'parwaaz.lang';

function initialLang(): string {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && tenant.product.languages.includes(stored)) return stored;
  } catch {
    // localStorage unavailable (private mode, SSR, ...) — fall through to the tenant default.
  }
  return tenant.product.defaultLanguage;
}

export const lang = signal<string>(initialLang());

function applyDocumentLang(value: string) {
  if (typeof document !== 'undefined') document.documentElement.lang = value;
}

applyDocumentLang(lang.value);

export function setLang(next: string) {
  if (!tenant.product.languages.includes(next)) return;
  lang.value = next;
  applyDocumentLang(next);
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    // ignore — language just won't persist across reloads
  }
}

/** Returns a `t(key, vars)` function bound to the current language. Re-renders the caller when `lang` changes. */
export function useT() {
  const dict = getDict(lang.value);
  return (key: string, vars?: Record<string, string | number>) => translate(dict, key, vars);
}
